import IntroBlock from 'components/public/Intro/IntroBlock';
import capitalize from 'components/utils/capitalize';
import React from 'react';
import ReactPlayer from 'react-player';
import { Hitch } from 'services/apps/hitches';
import videos from 'services/apps/videos';
import styles from './styles.module.css';

export type InterestState = {
  state:
    | 'one-app-supported'
    | 'one-app-not-supported'
    | 'two-app-supported'
    | 'two-app-one-supported'
    | 'two-app-not-supported'
    | 'no-app';
  supportedAppName?: string;
  secondarySupportedAppName?: string;
  unsupportedAppName?: string;
  secondaryUnsupportedAppName?: string;
  oneAppSelectedAppName?: string;
  firstApp?: Hitch;
  secondApp?: Hitch;
  hitchId?: string;
};

export const createIntro = (
  interestState: InterestState,
  formatMessage: any
): {
  introTitle: string;
  introDescription: JSX.Element;
  introVideo?: JSX.Element;
  demoVideo?: JSX.Element;
} => {
  const oneNotSupported = formatMessage({
    defaultMessage: "Can't find what you're looking for?",
    description: 'integrations/one-not-supported/title',
  });

  const contactUsText = formatMessage({
    defaultMessage: 'Contact us and we will help you save time and resources.',
    description: 'integrations/contact/description',
  });

  const automateWorkflowText = formatMessage({
    defaultMessage: 'Automate your workflow',
    description: 'integrations/workflow/title',
  });

  const subscribeText = formatMessage({
    defaultMessage: 'Get notified',
    description: 'integrations/notified/title',
  });

  const supportedTitle = formatMessage({
    defaultMessage: 'Ask a change',
    description: 'integrations/change/title',
  });

  const twoAppSupportedDescription = formatMessage(
    {
      defaultMessage: `We support the {app1} - {app2} integration for other clients, and are currently working on making this
    publicly available.`,
      description: 'integrations/two-app-supported/description/1',
    },
    {
      app1: interestState.supportedAppName,
      app2: interestState.secondarySupportedAppName,
    }
  );

  const twoAppOneSupportedDescription = formatMessage(
    {
      defaultMessage: `We fully support {app1} for other clients, and are currently planning to integrate {app2}.`,
      description: 'integrations/two-app-one-supported/description/1',
    },
    {
      app1: interestState.supportedAppName,
      app2: interestState.unsupportedAppName,
    }
  );

  const integrationChangeRequestQuestion = formatMessage({
    defaultMessage: 'You miss something to make it work for you?',
    description: 'integrations/app-change-request/question/interest',
  });

  const integrationChangeRequestText = formatMessage({
    defaultMessage: 'Talk to us, so we can make you happy.',
    description: 'integrations/app-change-request/subscribe',
  });

  const integrationInterestQuestion = formatMessage({
    defaultMessage: 'Are you interested in this integration?',
    description: 'integrations/app-support/question/interest',
  });

  const integrationSubscribeText = formatMessage({
    defaultMessage: 'Subscribe, so we can let you know when it is available.',
    description: 'integrations/app-support/subscribe',
  });

  switch (interestState.state) {
    case 'one-app-supported':
      return {
        introTitle: oneNotSupported,
        introDescription: (
          <>
            <p>
              {formatMessage(
                {
                  defaultMessage: `Don't worry, as we already integrate with {app}, it could be a peace of cake to support your other integration as well.`,
                  description: 'integrations/one-supported/description/1',
                },
                { app: interestState.supportedAppName }
              )}
            </p>
            <p>
              {formatMessage({
                defaultMessage: 'We are here to help you.',
                description: 'integrations/one-supported/description/2',
              })}
            </p>
            <p>{contactUsText}</p>
          </>
        ),
      };
    case 'one-app-not-supported':
      return {
        introTitle: oneNotSupported,
        introDescription: (
          <>
            <p>
              {formatMessage(
                {
                  defaultMessage: `Don't worry, as we are planning to integrate with {app}, we can take into account your specific case and already support your other integration as well`,
                  description: 'integrations/one-not-supported/description/1',
                },
                { app: interestState.unsupportedAppName }
              )}
            </p>

            <p>{contactUsText})</p>
          </>
        ),
      };
    case 'two-app-supported':
      return {
        introTitle: supportedTitle,
        introDescription: (
          <>
            <p>{twoAppSupportedDescription}</p>
            <p>{integrationChangeRequestQuestion}</p>
            <p>{integrationChangeRequestText}</p>
          </>
        ),
        demoVideo:
          interestState.hitchId &&
          videos[interestState.hitchId] &&
          videos[interestState.hitchId]['how-to'] ? (
            <IntroBlock
              title={formatMessage({
                defaultMessage: 'How it works',
                description: 'integrations/supported/demo/title',
              })}
              subTitle={formatMessage(videos[interestState.hitchId]['how-to'].title)}
              type="green-top"
              description={
                <ReactPlayer url={videos[interestState.hitchId]['how-to'].url} controls={true} />
              }
            />
          ) : (
            <></>
          ),
        introVideo:
          interestState.hitchId &&
          videos[interestState.hitchId] &&
          videos[interestState.hitchId]['intro'] ? (
            <IntroBlock
              className={styles.introVideo}
              type="white"
              description={
                <ReactPlayer
                  playing
                  muted
                  loop
                  url={videos[interestState.hitchId]['intro'].url}
                  controls={true}
                  config={{
                    youtube: {
                      playerVars: { showinfo: 1 },
                    },
                  }}
                />
              }
            />
          ) : (
            <></>
          ),
      };
    case 'two-app-one-supported':
      return {
        introTitle: subscribeText,
        introDescription: (
          <>
            <p>{twoAppOneSupportedDescription}</p>
            <p>{integrationInterestQuestion}</p>
            <p>{integrationSubscribeText}</p>
          </>
        ),
      };
    case 'two-app-not-supported':
      return {
        introTitle: automateWorkflowText,
        introDescription: (
          <>
            <p>
              {formatMessage(
                {
                  defaultMessage: `We are still planning to support {app1} and {app2}.`,
                  description: 'integrations/two-app-not-supported/description/1',
                },
                {
                  app1: interestState.unsupportedAppName,
                  app2: interestState.secondaryUnsupportedAppName,
                }
              )}
            </p>
            <p>
              {formatMessage({
                defaultMessage:
                  "You need this integration? Let us know, so we can support your use case and change our prio's accordingly.",
                description: 'integrations/two-app-not-supported/question',
              })}
            </p>
            <p>
              {formatMessage({
                defaultMessage: 'Or just subscribe, so we can inform you when we are ready.',
                description: 'integrations/two-app-not-supported/subscribe',
              })}
            </p>
          </>
        ),
      };
    default:
      return {
        introTitle: automateWorkflowText,
        introDescription: (
          <>
            <p>
              {formatMessage({
                defaultMessage: `Choose your integrations from the list.`,
                description: 'integrations/app-select/description/1',
              })}
            </p>
            <p>
              {formatMessage({
                defaultMessage:
                  "You don't find them? Let us know, we are always open to support others as well.",
                description: 'integrations/app-select/subscribe',
              })}
            </p>
          </>
        ),
      };
  }
};

export function createHitchId(hitch1?: string, hitch2?: string): string | undefined {
  if (!hitch1 || !hitch2) {
    return undefined;
  }
  return [hitch1, hitch2]
    .sort()
    .reduce((prevValue, hitch, index) => (index === 0 ? hitch : `${prevValue}$$${hitch}`), '');
}

export function parseHitchId(hitchId: string): string[] {
  return hitchId ? hitchId.split('$$') : [];
}

export function createTitleFromHitchId(hitchId: string): string {
  return parseHitchId(hitchId).reduce((prevValue, currValue, index) => {
    if (index === 0) {
      if (currValue) {
        return capitalize(currValue);
      }
      return '';
    } else {
      if (currValue) {
        return `${prevValue ? `${prevValue} - ` : ''}${capitalize(currValue)}`;
      }
      return prevValue;
    }
  }, '');
}

export function getInterestState(mainHitch?: Hitch, secondaryHitch?: Hitch): InterestState {
  const hitchId = createHitchId(mainHitch?.id, secondaryHitch?.id);
  // const categoryId = createHitchId(mainHitch?.category[0], secondaryHitch?.category[0]);
  if (mainHitch) {
    switch (mainHitch.status) {
      case 'supported':
        if (!secondaryHitch) {
          return {
            state: 'one-app-supported',
            supportedAppName: mainHitch.name,
            oneAppSelectedAppName: mainHitch.name,
            firstApp: mainHitch,
            hitchId,
          };
        }
        switch (secondaryHitch.status) {
          case 'supported':
            return {
              state: 'two-app-supported',
              supportedAppName: mainHitch.name,
              secondarySupportedAppName: secondaryHitch.name,
              firstApp: mainHitch,
              secondApp: secondaryHitch,
              hitchId,
            };

          default:
            return {
              state: 'two-app-one-supported',
              supportedAppName: mainHitch.name,
              unsupportedAppName: secondaryHitch.name,
              firstApp: mainHitch,
              secondApp: secondaryHitch,
              hitchId,
            };
        }

      default:
        if (!secondaryHitch) {
          return {
            state: 'one-app-not-supported',
            unsupportedAppName: mainHitch.name,
            oneAppSelectedAppName: mainHitch.name,
            firstApp: mainHitch,
            hitchId,
          };
        }
        switch (secondaryHitch.status) {
          case 'supported':
            return {
              state: 'two-app-one-supported',
              unsupportedAppName: mainHitch.name,
              supportedAppName: secondaryHitch.name,
              firstApp: mainHitch,
              secondApp: secondaryHitch,
              hitchId,
            };

          default:
            return {
              state: 'two-app-not-supported',
              unsupportedAppName: mainHitch.name,
              secondaryUnsupportedAppName: secondaryHitch.name,
              firstApp: mainHitch,
              secondApp: secondaryHitch,
              hitchId,
            };
        }
    }
  } else {
    if (!secondaryHitch) {
      return { state: 'no-app', hitchId };
    }
    switch (secondaryHitch.status) {
      case 'supported':
        return {
          state: 'one-app-supported',
          oneAppSelectedAppName: secondaryHitch.name,
          supportedAppName: secondaryHitch.name,
          firstApp: secondaryHitch,
          hitchId,
        };

      default:
        return {
          state: 'one-app-not-supported',
          oneAppSelectedAppName: secondaryHitch.name,
          unsupportedAppName: secondaryHitch.name,
          firstApp: secondaryHitch,
          hitchId,
        };
    }
  }
}
